import React, { type FC, type PropsWithChildren, type ReactNode, useState } from 'react'
import { Box, Stack, type SxProps, type Theme } from '@mui/material'

import { RecordPanelOutletProvider, useIsMobile } from '../common'
import { LinearProgressLoader } from '../LinearProgress'
import { MobileNavigation } from './MobileNavigation'
import { SideNavigation } from './SideNavigation'
import { TopNavigation } from './TopNavigation'

const wrapperStyle: SxProps<Theme> = { height: '100vh', backgroundColor: 'grey.100' }
const sideColumnStyle: SxProps<Theme> = { height: '100%', flexShrink: 0, overflowX: 'hidden', overflowY: 'auto' }
const mainColumnStyle: SxProps<Theme> = { height: '100%', minWidth: 0, flexGrow: 1 }
/** The record panel outlet: positioned, and clipping the panel while it slides */
const contentWrapperStyle: SxProps<Theme> = { position: 'relative', flexGrow: 1, minHeight: 0, overflow: 'hidden' }

type AppNavigationProps = PropsWithChildren<Readonly<{ deviationsBadge?: ReactNode, mobileDeviationsBadge?: ReactNode }>>

/**
 * The frame: the side bar runs the full height on the left, and the top bar, the loading bar
 * and the page sit in a column to its right. On a small screen the mobile bar replaces both, and the page keeps its place.
 * `deviationsBadge` (the side bar's) and `mobileDeviationsBadge` (the mobile bar's) are optional slots, because
 * `Navigation/` and `DeviationNotifications/` are separate feature folders.
 */
export const AppNavigation: FC<AppNavigationProps> = ({ deviationsBadge, mobileDeviationsBadge, children }) => {
    const isMobile = useIsMobile()
    const [recordPanelOutlet, setRecordPanelOutlet] = useState<HTMLElement | null>(null)

    return (
        <Stack direction="row" sx={wrapperStyle}>
            {!isMobile && (
                <Box sx={sideColumnStyle}>
                    <SideNavigation deviationsBadge={deviationsBadge} />
                </Box>
            )}
            <Stack sx={mainColumnStyle}>
                {isMobile ? <MobileNavigation deviationsBadge={mobileDeviationsBadge} /> : <TopNavigation />}
                <LinearProgressLoader />
                <Stack ref={setRecordPanelOutlet} direction="row" sx={contentWrapperStyle}>
                    <RecordPanelOutletProvider outlet={recordPanelOutlet}>{children}</RecordPanelOutletProvider>
                </Stack>
            </Stack>
        </Stack>
    )
}
